/**
 * MFA Verification Component
 *
 * Handles multi-factor authentication verification during sign-in.
 * Shows when a user has MFA enabled and needs to verify their second factor.
 */

import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Shield, AlertCircle, Loader2, ArrowLeft, Smartphone } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface MFAVerificationProps {
  onSuccess: () => void;
}

export default function MFAVerification({ onSuccess }: MFAVerificationProps) {
  const { verifyMFACode, cancelMFA, mfaState, error, clearError } = useAuth();

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Auto-submit when all digits are entered
  useEffect(() => {
    const fullCode = code.join('');
    if (fullCode.length === 6 && code.every((digit) => digit !== '')) {
      handleVerify();
    }
  }, [code]);

  const handleChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then((text) => {
        const digits = text.replace(/\D/g, '').slice(0, 6).split('');
        const newCode = [...code];
        digits.forEach((digit, i) => {
          if (i < 6) newCode[i] = digit;
        });
        setCode(newCode);
        inputRefs.current[Math.min(digits.length, 5)]?.focus();
      });
    }
  };

  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) return;

    setVerifying(true);
    clearError();

    try {
      await verifyMFACode(fullCode);
      onSuccess();
    } catch (err) {
      // Check if this is the "code sent" message
      if (err instanceof Error && err.message.includes('Verification code sent')) {
        setCodeSent(true);
      } else {
        // Clear the code on error
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleCancel = () => {
    cancelMFA();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="sm:mx-auto sm:w-full sm:max-w-md"
      >
        {/* Back Button */}
        <button
          onClick={handleCancel}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to sign in
        </button>

        <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100">
          {/* Icon */}
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-purple-600" />
          </div>

          <h2 className="text-center text-2xl font-bold text-gray-900 mb-2">
            Two-Factor Authentication
          </h2>

          <p className="text-center text-gray-600 mb-8">
            {codeSent
              ? 'Enter the 6-digit code sent to your phone.'
              : 'Verify your identity with your second factor.'}
          </p>

          {/* Phone Hint */}
          {mfaState.resolver?.hints[0] && (
            <div className="flex items-center justify-center gap-2 mb-6 text-sm text-gray-500">
              <Smartphone className="w-4 h-4" />
              <span>
                Code will be sent to{' '}
                {(mfaState.resolver.hints[0] as { phoneNumber?: string }).phoneNumber ||
                  'your phone'}
              </span>
            </div>
          )}

          {/* Error Message */}
          {error && !error.includes('Verification code sent') && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3"
            >
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </motion.div>
          )}

          {/* Code Input */}
          <div className="flex justify-center gap-2 mb-6">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={verifying}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition disabled:opacity-50"
              />
            ))}
          </div>

          {/* Verify Button */}
          <button
            onClick={handleVerify}
            disabled={verifying || code.some((d) => !d)}
            className="w-full bg-purple-600 text-white py-3 rounded-xl font-semibold hover:bg-purple-700 focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {verifying ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Verifying...
              </>
            ) : codeSent ? (
              'Verify Code'
            ) : (
              'Send Verification Code'
            )}
          </button>

          {/* Resend Code */}
          {codeSent && (
            <button
              onClick={() => {
                setCode(['', '', '', '', '', '']);
                setCodeSent(false);
                handleVerify();
              }}
              className="w-full mt-4 text-purple-600 font-medium hover:text-purple-700 transition"
            >
              Resend code
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
